import { type CellIndex, type Dir, isOpposite, manhattan, neighbors4, stepIndex } from './grid';
import { segmentIdeals, sumIdeal } from './quiz';
import { pickReviveQuiz } from './revive';
import { cursor } from './rng';
import type { EngineRules } from './rules';
import { nextAnswerTile, relocateTiles, spawnTiles, tileAt } from './spawn';
import { findQuestion, freshWord } from './state';
import {
  type DeathCause,
  type EndReason,
  EngineContractError,
  type EngineCtx,
  type EngineEvent,
  type EngineState,
  type LenLedger,
  type PenaltyState,
  type ReviveState,
  type SnakeState,
  type Tile,
  type WordOutcome,
  type WordRecord,
  type WordState,
} from './types';

/** 진행 방향으로 벽·몸통에 막히기 전까지 남은 칸 수. 틱당 1회만 계산한다. */
export function freeAheadOf(cells: readonly CellIndex[], dir: Dir): number {
  let n = 0;
  let i = cells[0] as CellIndex;
  const body = new Set<CellIndex>(cells);
  for (;;) {
    i = stepIndex(i, dir);
    if (i < 0 || body.has(i)) return n;
    if (++n >= 32) return n;
  }
}

export function freeAhead(s: EngineState): number {
  return freeAheadOf(s.snake.cells, s.snake.dir);
}

function armPenalty(p: PenaltyState, free: number, r: EngineRules): PenaltyState {
  const ticksLeft = Math.max(p.ticksLeft, r.penaltyTicks);
  if (p.status === 2) return { status: 2, ticksLeft, deferTicksLeft: 0 };
  if (free >= r.penaltyArmMinCells) return { status: 2, ticksLeft, deferTicksLeft: 0 };
  return {
    status: 1,
    ticksLeft,
    deferTicksLeft: p.status === 1 ? p.deferTicksLeft : r.penaltyDeferMaxTicks,
  };
}

function tickPenalty(p: PenaltyState, free: number, r: EngineRules): PenaltyState {
  if (p.status === 0) return p;
  if (p.status === 2) {
    if (free <= r.penaltyAbortCells) {
      return { status: 1, ticksLeft: p.ticksLeft, deferTicksLeft: r.penaltyDeferMaxTicks };
    }
    const left = p.ticksLeft - 1;
    return left <= 0
      ? { status: 0, ticksLeft: 0, deferTicksLeft: 0 }
      : { status: 2, ticksLeft: left, deferTicksLeft: 0 };
  }
  if (free >= r.penaltyArmMinCells) return { status: 2, ticksLeft: p.ticksLeft, deferTicksLeft: 0 };
  const d = Math.max(0, p.deferTicksLeft - 1);
  if (d === 0 && free > r.penaltyAbortCells) {
    return { status: 2, ticksLeft: p.ticksLeft, deferTicksLeft: 0 };
  }
  return { status: 1, ticksLeft: p.ticksLeft, deferTicksLeft: d };
}

interface Mut {
  snake: { cells: CellIndex[]; dir: Dir; pending: number };
  tiles: Tile[];
  word: WordState;
  words: readonly string[];
  sessionSeed: string;
  runIndex: number;
  penalty: PenaltyState;
  revive: ReviveState;
  reviveTicks: number;
  rng: { quiz: number; layout: number; decoy: number; respawn: number; revive: number };
  records: WordRecord[];
  events: EngineEvent[];
  readyTicksLeft: number;
  clearTicksLeft: number;
  moveTicks: number;
  growTicks: number;
  growPauseLeft: number;
  peakLength: number;
  lastSolveTick: number;
  ledger: LenLedger;
  spawnDegraded: number;
  spawnRelaxed: number;
  nominalMs: number;
  wordClosedThisTick: boolean;
  phase: 'play' | 'revive' | 'over';
  endReason: EndReason;
}

const logical = (m: Mut): number => m.snake.cells.length + m.snake.pending;
/** 함수 호출로 읽어 TS 의 지역 좁히기(반환 후에도 'revive' 로 고정)를 피한다. */
const phaseOf = (m: Mut): Mut['phase'] => m.phase;

function pushSpawnEvents(m: Mut, events: readonly EngineEvent[]): void {
  for (const e of events) {
    if (e.kind === 'spawn_degraded') m.spawnDegraded++;
    if (e.kind === 'spawn_relaxed') m.spawnRelaxed++;
    m.events.push(e);
  }
}

function makeRecord(m: Mut, outcome: WordOutcome, endTick: number): WordRecord {
  const w = m.word;
  return {
    index: w.index,
    questionId: w.questionId,
    answerLen: w.answer.length,
    outcome,
    showTick: w.showTick,
    spawnTick: w.spawnTick,
    endTick,
    movedTicks: w.movedTicks,
    idealTicks: w.idealTicks,
    segMoved: w.segMoved,
    segIdeal: w.segIdeal,
    wrongEats: w.wrongEats,
    decoyEats: w.decoyEats,
    outOfOrderEats: w.outOfOrderEats,
    firstTurnTick: w.firstTurnTick,
    relocations: w.relocations,
    reviveInWord: w.reviveInWord,
    wordTick: w.wordTick,
    perfect: w.perfect,
    len0: w.len0,
    len1: logical(m),
    autoGrown: w.autoGrown,
    wrongGrown: w.wrongGrown,
    nominalMs: m.nominalMs,
    wrongJamoSim: w.wrongJamoSim,
  };
}

/** 판 종료. 진행 중이던 문제가 있으면 died 로 닫고 word_end 를 반드시 발사한다(사망 행 100% 손실 방지). */
function over(m: Mut, reason: EndReason, tick: number): void {
  if (!m.wordClosedThisTick && m.word.eaten < m.word.answer.length && m.clearTicksLeft === 0) {
    const rec = makeRecord(m, 'died', tick);
    m.records.push(rec);
    m.events.push({ kind: 'word_end', record: rec });
    m.wordClosedThisTick = true;
  }
  m.phase = 'over';
  m.endReason = reason;
  m.events.push({ kind: 'game_over', reason });
}

/**
 * 사망 분기(RULES §1.14). 부활 기회가 남았으면 revive 페이즈로 — **기록을 남기지 않는다**(문제는 아직 안 끝났다).
 * 벽·자기 몸 두 경로에서만 불린다. engorged·tick-limit 은 부활 대상이 아니다.
 */
function die(m: Mut, ctx: EngineCtx, cause: DeathCause, tick: number): void {
  const R = ctx.rules;
  if (R.reviveEnabled && m.revive.offered < R.reviveMaxOffers) {
    const drawn = pickReviveQuiz(
      { words: m.words, sessionSeed: m.sessionSeed, runIndex: m.runIndex, rngRevive: m.rng.revive },
      ctx,
    );
    m.rng.revive = drawn.rng;
    m.revive = {
      status: 1,
      offered: m.revive.offered + 1,
      used: m.revive.used,
      cause,
      questionId: drawn.quiz.id,
      choices: drawn.quiz.choices,
      correctIndex: drawn.quiz.correctIndex,
      picked: -1,
      armTicksLeft: R.reviveArmTicks,
      ticksLeft: R.reviveLimitTicks,
      revealTicksLeft: 0,
      openTick: tick,
      answerTick: -1,
      result: 'none',
    };
    m.phase = 'revive';
    m.events.push({ kind: 'revive_offer', questionId: drawn.quiz.id, cause });
    return;
  }
  over(m, cause, tick);
}

/** 남은 정답 체인으로 우회율 분모를 (재)봉인한다. 이미 먹은 세그먼트는 보존한다. */
function sealIdeal(m: Mut): void {
  const head = m.snake.cells[0] as CellIndex;
  const cells: CellIndex[] = [];
  for (let k = m.word.eaten; ; k++) {
    const t = m.tiles.find((x) => x.kind === 0 && x.slot === k);
    if (t === undefined) break;
    cells.push(t.cell);
  }
  const rest = segmentIdeals(head, m.snake.dir, cells);
  const head0: number[] = [];
  for (let k = 0; k < m.word.eaten; k++) head0.push(m.word.segIdeal[k] ?? 0);
  const segIdeal = [...head0, ...rest];
  m.word = {
    ...m.word,
    segIdeal,
    idealTicks: sumIdeal(segIdeal),
    bestRemaining: cells.length > 0 ? manhattan(head, cells[0] as CellIndex) : 0,
    noProgressTicks: 0,
    sealedTicks: 0,
  };
}

function chainDist(head: CellIndex, cells: readonly CellIndex[]): number {
  let n = 0;
  let from = head;
  for (const c of cells) {
    n += manhattan(from, c);
    from = c;
  }
  return n;
}

function remainingAnswerCells(tiles: readonly Tile[], eaten: number): CellIndex[] {
  const out: CellIndex[] = [];
  for (let k = eaten; ; k++) {
    const t = tiles.find((x) => x.kind === 0 && x.slot === k);
    if (t === undefined) break;
    out.push(t.cell);
  }
  return out;
}

/**
 * 재배치·부활 시 우회율 분모 — 부호 있는 차분의 누적(SPEC §10.3). 전면 재계산은 자기참조다.
 * 머리가 순간이동하는 경우(부활)를 담도록 from/to 머리를 따로 받는다.
 */
function rebaseIdeal(
  m: Mut,
  fromHead: CellIndex,
  toHead: CellIndex,
  oldChain: readonly CellIndex[],
  newChain: readonly CellIndex[],
): void {
  const before = chainDist(fromHead, oldChain);
  const after = chainDist(toHead, newChain);
  let ideal = Math.max(1, m.word.idealTicks + (after - before));
  const head0: number[] = [];
  for (let k = 0; k < m.word.eaten; k++) head0.push(m.word.segIdeal[k] ?? 0);
  const rest: number[] = [];
  let from = toHead;
  for (const c of newChain) {
    rest.push(manhattan(from, c));
    from = c;
  }
  const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);
  if (rest.length > 0) {
    const adjusted = (rest[0] as number) + (ideal - sum(head0) - sum(rest));
    rest[0] = Math.max(0, adjusted);
    if (adjusted < 0) ideal = sum(head0) + sum(rest);
  } else {
    ideal = Math.max(1, sum(head0));
  }
  m.word = {
    ...m.word,
    segIdeal: [...head0, ...rest],
    idealTicks: ideal,
    bestRemaining: newChain.length > 0 ? manhattan(toHead, newChain[0] as CellIndex) : 0,
    noProgressTicks: 0,
    sealedTicks: 0,
  };
}

/** 다음 문제 스폰. 마지막이었으면 봉인 여부로 판을 닫는다. */
function spawnNextWord(m: Mut, ctx: EngineCtx, tick: number): void {
  const nextIndex = m.word.index + 1;
  if (nextIndex >= ctx.rules.runWordCount) {
    let solved = 0;
    for (const rec of m.records) if (rec.outcome === 'solved') solved++;
    m.phase = 'over';
    m.endReason = solved === ctx.rules.runWordCount ? 'clear' : 'unsealed';
    m.events.push({ kind: 'game_over', reason: m.endReason });
    return;
  }
  const q = findQuestion(ctx, m.words[nextIndex] as string);
  const layout = cursor(m.rng.layout);
  const decoy = cursor(m.rng.decoy);
  const spawn = spawnTiles(
    {
      head: m.snake.cells[0] as CellIndex,
      dir: m.snake.dir,
      body: m.snake.cells,
      question: q,
      rules: ctx.rules,
      alphabet: ctx.alphabet,
    },
    layout,
    decoy,
  );
  m.rng.layout = layout.s;
  m.rng.decoy = decoy.s;
  m.tiles = [...spawn.tiles];
  m.word = freshWord(nextIndex, q, tick, logical(m));
  m.events.push({ kind: 'word_spawn', index: nextIndex, id: q.id, idealTicks: 0 });
  pushSpawnEvents(m, spawn.events);
  sealIdeal(m);
}

function closeWord(m: Mut, ctx: EngineCtx, tick: number, outcome: WordOutcome): void {
  if (m.wordClosedThisTick) return;
  m.wordClosedThisTick = true;
  const rec = makeRecord(m, outcome, tick);
  m.records.push(rec);
  m.events.push({ kind: 'word_end', record: rec });
  if (outcome === 'solved') m.lastSolveTick = tick;
  // 재조준 구간 동안 끝난 문제의 오답이 남으면 재개 순간 필드가 통째로 갈린다
  m.tiles = [];
  m.clearTicksLeft = ctx.rules.wordClearTicks;
  if (m.clearTicksLeft === 0) spawnNextWord(m, ctx, tick);
}

/** 섭취 판정. 길이 변화는 여기서 하지 않고 플래그만 남긴다(5단계 순서를 지키기 위해). */
function eat(m: Mut, tile: Tile, tick: number, ctx: EngineCtx): 'correct' | 'wrong' | 'none' {
  const r = ctx.rules;
  const w = m.word;
  if (tile.kind === 0 && tile.slot === w.eaten) {
    m.word = {
      ...w,
      eaten: w.eaten + 1,
      segMoved: [...w.segMoved, w.movedTicks - w.segBoundary],
      segBoundary: w.movedTicks,
      bestRemaining: Number.MAX_SAFE_INTEGER,
      noProgressTicks: 0,
      sealedTicks: 0,
    };
    m.tiles = m.tiles.filter((t) => t.cell !== tile.cell);
    m.events.push({ kind: 'eat_correct', slot: tile.slot, cell: tile.cell });
    return 'correct';
  }
  if (tick - w.spawnTick < r.transitionGraceTicks) return 'none';

  const atFloor = logical(m) <= r.snakeLenMin;
  const sim = ctx.alphabet.similarity(tile.ch, w.answer.charAt(w.eaten));
  const firstHit = w.hitCells.indexOf(tile.cell) < 0;
  m.word = {
    ...w,
    wrongEats: w.wrongEats + 1,
    decoyEats: tile.kind === 1 ? w.decoyEats + 1 : w.decoyEats,
    outOfOrderEats: tile.kind === 0 ? w.outOfOrderEats + 1 : w.outOfOrderEats,
    wrongJamoSim: [...w.wrongJamoSim, sim],
    hitCells: firstHit ? [...w.hitCells, tile.cell] : w.hitCells,
    perfect: false,
  };
  m.penalty = armPenalty(m.penalty, freeAheadOf(m.snake.cells, m.snake.dir), r);
  m.events.push({
    kind: 'eat_wrong',
    cell: tile.cell,
    ch: tile.ch,
    slot: tile.kind === 0 ? tile.slot : -1,
    jamoSim: sim,
    atFloor,
    firstHit,
  });
  return 'wrong';
}

function relocate(m: Mut, ctx: EngineCtx, tick: number, reason: 'no-progress' | 'sealed'): void {
  const count = m.word.relocations + 1;
  // 부활 재배치는 예산을 잠식하지 않는다 — 안 빼면 부활 판의 문제가 한 번 일찍 skipped 로 닫힌다
  if (count - m.word.reviveRelocations > ctx.rules.relocateMaxPerWord) {
    closeWord(m, ctx, tick, 'skipped');
    return;
  }
  const head = m.snake.cells[0] as CellIndex;
  const oldChain = remainingAnswerCells(m.tiles, m.word.eaten);
  const respawn = cursor(m.rng.respawn);
  const out = relocateTiles(
    m.tiles,
    {
      head,
      dir: m.snake.dir,
      body: m.snake.cells,
      question: { id: m.word.questionId, q: '', a: m.word.answer, d: [] },
      rules: ctx.rules,
      alphabet: ctx.alphabet,
    },
    respawn,
  );
  m.tiles = [...out.tiles];
  pushSpawnEvents(m, out.events);
  m.rng.respawn = respawn.s;
  m.word = { ...m.word, relocations: count };
  rebaseIdeal(m, head, head, oldChain, remainingAnswerCells(m.tiles, m.word.eaten));
  m.events.push({ kind: 'relocate', reason, count });
}

/**
 * 부활 적용(RULES §1.14). 논리 길이는 보존한다 — 물리 셀 min(논리, snakeLenInit) 을 시작 위치에 놓고
 * 나머지는 pending 으로 되살아난다. 길이를 줄여 주면 상한 근처에서 일부러 죽는 것이 최적이 된다.
 */
function applyRevive(m: Mut, ctx: EngineCtx, tick: number): void {
  const R = ctx.rules;
  const deathHead = m.snake.cells[0] as CellIndex;
  const oldChain = remainingAnswerCells(m.tiles, m.word.eaten);
  const total = logical(m);

  // (1) 뱀 재생성 — 고정 위치·방향, 논리 길이 보존
  const bodyCells = Math.min(total, R.snakeLenInit);
  const back = ((R.snakeStartDir + 2) & 3) as Dir;
  const cells: CellIndex[] = [R.snakeStartCell];
  let i = R.snakeStartCell;
  for (let n = 1; n < bodyCells; n++) {
    i = stepIndex(i, back);
    cells.push(i);
  }
  m.snake = { cells, dir: R.snakeStartDir, pending: total - cells.length };

  // (2) 시작 위치 근처에 타일이 있으면 전 타일 재배치(부분 금지 — ADR-008). 예산 미잠식
  if (m.tiles.some((t) => manhattan(R.snakeStartCell, t.cell) < R.tileHeadMinDist)) {
    const respawn = cursor(m.rng.respawn);
    const out = relocateTiles(
      m.tiles,
      {
        head: R.snakeStartCell,
        dir: R.snakeStartDir,
        body: cells,
        question: { id: m.word.questionId, q: '', a: m.word.answer, d: [] },
        rules: R,
        alphabet: ctx.alphabet,
      },
      respawn,
    );
    m.tiles = [...out.tiles];
    pushSpawnEvents(m, out.events);
    m.rng.respawn = respawn.s;
    m.word = { ...m.word, reviveRelocations: m.word.reviveRelocations + 1 };
    m.events.push({ kind: 'relocate', reason: 'revive', count: m.word.relocations });
  }

  // (3) 우회율 분모 — 순간이동을 부호 있는 차분으로 흡수
  rebaseIdeal(
    m,
    deathHead,
    R.snakeStartCell,
    oldChain,
    remainingAnswerCells(m.tiles, m.word.eaten),
  );

  // (4) 회복과 재개 — 페널티 해제 · 성장 시계는 그대로(죽음의 값은 시간이다) · READY 정지
  m.penalty = { status: 0, ticksLeft: 0, deferTicksLeft: 0 };
  m.readyTicksLeft = R.reviveReadyTicks;
  m.clearTicksLeft = 0;
  m.word = { ...m.word, reviveInWord: true };
  m.phase = 'play';
  m.revive = { ...m.revive, status: 0, used: m.revive.used + 1, revealTicksLeft: 0 };
  m.events.push({ kind: 'revive_done', used: true });
  void tick;
}

/** 부활 페이즈 한 틱 — 1~8단계를 전부 건너뛴다. `dir` 은 무시한다. */
function reviveStep(m: Mut, ctx: EngineCtx, choice: number | null, tick: number): void {
  const R = ctx.rules;
  const rv = m.revive;
  m.reviveTicks++;

  if (rv.status === 1) {
    if (rv.armTicksLeft > 0) {
      // (a) 오조작 방지 — 제한 시간 미소모
      m.revive = { ...rv, armTicksLeft: rv.armTicksLeft - 1 };
      return;
    }
    // (b) 범위 밖은 미선택 취급(로그 오염 방어)
    const pick =
      choice !== null && Number.isInteger(choice) && choice >= 0 && choice < R.reviveChoiceCount
        ? choice
        : -1;
    if (pick >= 0) {
      const ok = pick === rv.correctIndex;
      m.revive = {
        ...rv,
        status: ok ? 2 : 3,
        picked: pick,
        answerTick: tick,
        result: ok ? 'correct' : 'wrong',
        revealTicksLeft: R.reviveRevealTicks,
      };
      m.events.push({
        kind: 'revive_judge',
        result: ok ? 'correct' : 'wrong',
        picked: pick,
        answerTicks: tick - rv.openTick,
      });
      if (R.reviveRevealTicks === 0) finishReveal(m, ctx, tick);
      return;
    }
    // (c) 제한 시간
    const left = rv.ticksLeft - 1;
    if (left <= 0) {
      m.revive = {
        ...rv,
        ticksLeft: 0,
        status: 3,
        answerTick: tick,
        result: 'timeout',
        revealTicksLeft: R.reviveRevealTicks,
      };
      m.events.push({
        kind: 'revive_judge',
        result: 'timeout',
        picked: -1,
        answerTicks: tick - rv.openTick,
      });
      if (R.reviveRevealTicks === 0) finishReveal(m, ctx, tick);
      return;
    }
    m.revive = { ...rv, ticksLeft: left };
    return;
  }

  // status 2/3 — 정답 공개
  const left = rv.revealTicksLeft - 1;
  if (left > 0) {
    m.revive = { ...rv, revealTicksLeft: left };
    return;
  }
  finishReveal(m, ctx, tick);
}

function finishReveal(m: Mut, ctx: EngineCtx, tick: number): void {
  if (m.revive.status === 2) applyRevive(m, ctx, tick);
  else {
    m.revive = { ...m.revive, revealTicksLeft: 0 };
    m.events.push({ kind: 'revive_done', used: false });
    over(m, m.revive.cause ?? 'wall', tick);
  }
}

export function step(
  state: EngineState,
  dir: Dir | null,
  ctx: EngineCtx,
  choice: number | null = null,
): EngineState {
  if (state.engineRulesHash !== ctx.engineRulesHash) {
    throw new EngineContractError('engineRulesHash', state.engineRulesHash, ctx.engineRulesHash);
  }
  if (state.packHash !== ctx.packHash) {
    throw new EngineContractError('packHash', state.packHash, ctx.packHash);
  }
  if (state.phase === 'over') return state;

  const r = ctx.rules;
  const tick = state.tick + 1;
  const m: Mut = {
    snake: { cells: [...state.snake.cells], dir: state.snake.dir, pending: state.snake.pending },
    tiles: [...state.tiles],
    word: state.word,
    words: state.words,
    sessionSeed: state.sessionSeed,
    runIndex: state.runIndex,
    penalty: state.penalty,
    revive: state.revive,
    reviveTicks: state.reviveTicks,
    rng: { ...state.rng },
    records: [...state.records],
    events: [],
    readyTicksLeft: state.readyTicksLeft,
    clearTicksLeft: state.clearTicksLeft,
    moveTicks: state.moveTicks,
    growTicks: state.growTicks,
    growPauseLeft: state.growPauseLeft,
    peakLength: state.peakLength,
    lastSolveTick: state.lastSolveTick,
    ledger: state.ledger,
    spawnDegraded: state.spawnDegraded,
    spawnRelaxed: state.spawnRelaxed,
    nominalMs: tick * r.nominalTickMs,
    wordClosedThisTick: false,
    phase: state.phase,
    endReason: state.endReason,
  };

  // R) 부활 페이즈 — 1~8 을 전부 건너뛴다
  if (m.phase === 'revive') {
    reviveStep(m, ctx, choice, tick);
    if (phaseOf(m) !== 'over' && tick >= r.runMaxTicks) over(m, 'tick-limit', tick);
    return commit(state, m, tick);
  }

  // 1) 방향 소비 — WORD_CLEAR 정지 구간에서는 소비하지 않는다. READY 는 READY_SKIP_ON_INPUT.
  const prevDir = m.snake.dir;
  if (dir !== null && m.clearTicksLeft === 0 && !isOpposite(m.snake.dir, dir)) m.snake.dir = dir;
  if (dir !== null && m.readyTicksLeft > 0) m.readyTicksLeft = 0;

  // 2) 머리 이동 — 정지 조건이면 카운터만 소모한다
  let justCleared = false;
  const halted = m.readyTicksLeft > 0 || m.clearTicksLeft > 0;
  if (halted) {
    if (m.readyTicksLeft > 0) m.readyTicksLeft--;
    else {
      m.clearTicksLeft--;
      if (m.clearTicksLeft === 0) justCleared = true;
    }
  } else {
    const head = m.snake.cells[0] as CellIndex;
    const nextHead = stepIndex(head, m.snake.dir);

    // 3) 충돌 판정 — 이번 틱에 실제로 비는 칸 수와 일치시킨다(정확한 vacating).
    if (nextHead < 0) {
      die(m, ctx, 'wall', tick);
      return commit(state, m, tick);
    }
    const vacating =
      m.snake.pending > 0 ? 0 : m.snake.pending < 0 && m.snake.cells.length > r.snakeLenMin ? 2 : 1;
    const bodyLen = m.snake.cells.length - vacating;
    for (let i = 0; i < bodyLen; i++) {
      if (m.snake.cells[i] === nextHead) {
        die(m, ctx, 'self', tick);
        return commit(state, m, tick);
      }
    }

    // 이동 소비 — 틱당 절대값 1칸
    m.snake.cells.unshift(nextHead);
    if (m.snake.pending > 0) m.snake.pending--;
    else {
      m.snake.cells.pop();
      if (m.snake.pending < 0 && m.snake.cells.length > r.snakeLenMin) {
        m.snake.cells.pop();
        m.snake.pending++;
      }
    }
    m.moveTicks++;
    m.word = { ...m.word, movedTicks: m.word.movedTicks + 1 };
    if (m.snake.dir !== prevDir && m.word.firstTurnTick < 0) {
      m.word = { ...m.word, firstTurnTick: tick };
    }

    // 4) 섭취 · 순서 판정
    const tile = tileAt(m.tiles, nextHead);
    const ate = tile === null ? 'none' : eat(m, tile, tick, ctx);

    // 5) 길이 경제 — 순서가 결정론 계약이다 (SEAL §5.A)
    // ① 오답 성장: 논리 길이 ≤ lenMax−1 클램프. engorged 를 만들 수 있는 것은 자동 성장뿐이다.
    if (ate === 'wrong') {
      const room = r.snakeLenMax - 1 - logical(m);
      const grow = Math.max(0, Math.min(r.growOnWrong, room));
      m.snake.pending += grow;
      m.ledger = { ...m.ledger, byWrong: m.ledger.byWrong + grow };
      m.word = { ...m.word, wrongGrown: m.word.wrongGrown + grow };
    }
    // ② 성장 시계 — 이동 틱만 센다. 해결 후 정지 구간에는 시계가 멈춘다.
    if (m.growPauseLeft > 0) m.growPauseLeft--;
    else {
      m.growTicks++;
      if (m.growTicks >= (r.growPeriodTable[m.word.index] as number)) {
        m.growTicks = 0;
        m.snake.pending += 1;
        m.ledger = { ...m.ledger, byTime: m.ledger.byTime + 1 };
        m.word = { ...m.word, autoGrown: m.word.autoGrown + 1 };
        m.events.push({ kind: 'grow', length: logical(m) });
      }
    }
    // ③ 정답·완성 수축 — 하한 클램프, 잉여 폐기
    if (ate === 'correct') {
      let shrink = r.shrinkOnCorrect;
      if (m.word.eaten === m.word.answer.length) {
        shrink += r.shrinkOnWord + (m.word.perfect ? r.shrinkOnPerfectWord : 0);
      }
      const before = logical(m);
      const applied = Math.min(shrink, Math.max(0, before - r.snakeLenMin));
      m.snake.pending -= applied;
      const wordPart = Math.max(0, applied - Math.min(applied, r.shrinkOnCorrect));
      m.ledger = {
        ...m.ledger,
        byAnswer: m.ledger.byAnswer - Math.min(applied, r.shrinkOnCorrect),
        byWord: m.ledger.byWord - wordPart,
      };
    }
    // ④ 상한 도달 = 판 종료 (사망이 아니다 — 부활 대상도 아니다)
    m.peakLength = Math.max(m.peakLength, logical(m));
    if (logical(m) >= r.snakeLenMax) {
      over(m, 'engorged', tick);
      return commit(state, m, tick);
    }
  }

  // 6) 페널티 상태 전이 — freeAhead 는 여기서 1회만 계산한다
  const free = freeAheadOf(m.snake.cells, m.snake.dir);
  const beforeStatus = m.penalty.status;
  m.penalty = tickPenalty(m.penalty, free, r);
  if (m.penalty.status !== beforeStatus) {
    m.events.push({ kind: 'penalty', status: m.penalty.status, ticksLeft: m.penalty.ticksLeft });
  }

  // 7) 단어 완성 처리 — 해결 시 성장 시계 정지(숨통)
  if (!halted && m.word.eaten === m.word.answer.length) {
    m.growPauseLeft = (r.growPeriodTable[m.word.index] as number) * r.growPauseOnWord;
    closeWord(m, ctx, tick, 'solved');
  } else if (justCleared) {
    spawnNextWord(m, ctx, tick);
  }

  // 8) 타이머 · 진전 · 봉인 · 재배치 (문제 타임아웃은 폐지 — 성장이 유일한 시계다)
  if (m.phase === 'play' && !halted) {
    m.word = { ...m.word, wordTick: m.word.wordTick + 1 };
    const target = nextAnswerTile(m.tiles, m.word.eaten);
    if (target !== null) {
      const head = m.snake.cells[0] as CellIndex;
      const d = manhattan(head, target.cell);
      m.word =
        d < m.word.bestRemaining
          ? { ...m.word, bestRemaining: d, noProgressTicks: 0 }
          : { ...m.word, noProgressTicks: m.word.noProgressTicks + 1 };
      const nb: number[] = [];
      neighbors4(target.cell, nb);
      const occupied = new Set<CellIndex>(m.snake.cells);
      let open = 0;
      for (const n of nb) if (!occupied.has(n)) open++;
      m.word = { ...m.word, sealedTicks: open === 0 ? m.word.sealedTicks + 1 : 0 };
      if (m.word.sealedTicks >= r.relocateSealedConfirmTicks) relocate(m, ctx, tick, 'sealed');
      else if (m.word.noProgressTicks >= r.relocateTicks) relocate(m, ctx, tick, 'no-progress');
    }
  }

  if (m.phase === 'play' && tick >= r.runMaxTicks) over(m, 'tick-limit', tick);

  return commit(state, m, tick);
}

function commit(prev: EngineState, m: Mut, tick: number): EngineState {
  const snake: SnakeState = { cells: m.snake.cells, dir: m.snake.dir, pending: m.snake.pending };
  return {
    ...prev,
    tick,
    nominalMs: m.nominalMs,
    phase: m.phase,
    endReason: m.endReason,
    readyTicksLeft: m.readyTicksLeft,
    clearTicksLeft: m.clearTicksLeft,
    snake,
    tiles: m.tiles,
    word: m.word,
    penalty: m.penalty,
    revive: m.revive,
    reviveTicks: m.reviveTicks,
    rng: m.rng,
    moveTicks: m.moveTicks,
    growTicks: m.growTicks,
    growPauseLeft: m.growPauseLeft,
    peakLength: m.peakLength,
    lastSolveTick: m.lastSolveTick,
    ledger: m.ledger,
    spawnDegraded: m.spawnDegraded,
    spawnRelaxed: m.spawnRelaxed,
    records: m.records,
    events: m.events,
  };
}

export function createStep(
  ctx: EngineCtx,
): (s: EngineState, d: Dir | null, choice?: number | null) => EngineState {
  return (s, d, choice = null) => step(s, d, ctx, choice);
}
